import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { ModuleState } from '@redux/modules/types'

import defaultRequestOptions from '@sections/Admin/packs/SystemManager/defaultRequestOptions'

const watchEntityKeys = ['apiKeys']

type ApiKeyFilters = {
  default_user_id?: number,
  owner_id?: number,
  owner_type?: number,
  string?: string,
}

type UseApiKeysOptions = {
  entityKey?: string,
  filters?: ApiKeyFilters,
  performHttpRequests?: boolean,
}

function useApiKeys(options: UseApiKeysOptions) {
  const { entityKey, filters = {} } = options
  const {
    default_user_id: filterUserId,
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
    string,
  } = filters

  const {
    updatedEntities: { apiKeys: apiKeysUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { apiKeys, users } = useSelector(reduxState => reduxState.entities)

  const filteredApiKeys = useMemo(() => {
    const filtered = Object.values(apiKeys).filter((apiKey) => {
      const {
        default_user_id,
        owner_id,
        owner_type,
      } = apiKey

      const user = default_user_id ? users[default_user_id] || {} : {}
      // @ts-expect-error TS2339
      const { full_name } = user

      const matchFullName = matchFilterString(full_name, string)
      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType ? String(owner_type) === String(filterOwnerType) : true
      const matchUserId = matchFilterNumber(default_user_id, filterUserId)

      return matchFullName && matchOwnerId && matchOwnerType && matchUserId
    })

    // @ts-expect-error TS2538
    return sortArrayBy(filtered, 'asc', ({ default_user_id }) => users[default_user_id]?.full_name)
  }, [apiKeysUpdatedAt, JSON.stringify(filters)])

  const filteredApiKeysCount = filteredApiKeys.length
  const hasFilteredApiKeys = !!filteredApiKeysCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredApiKeysCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingApiKeys } = useReduxAction(
    'apiKeys',
    'loadApiKeys',
    {
      entityKey,
      ...defaultRequestOptions.apiKey,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredApiKeys,
    filteredApiKeysCount,
    hasFilteredApiKeys,
    loading: loadingApiKeys,
  }
}

export default useApiKeys
